import { useEffect, useId, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, Eye, EyeOff, FileText, LockKeyhole, Mail } from "lucide-react";
import { Button } from "@/components/ax/Button";
import { Field } from "@/components/ax/Field";
import { BrandLogo } from "@/components/ax/BrandLogo";
import { useI18n } from "@/i18n";
import { useFlash } from "@/hooks/useFlash";
import { completeMfaLogin, getConsent, googleSignIn, login, sendPasswordReset } from "@/lib/mockAuth";
import { MfaChallenge } from "@/components/account/MfaChallenge";
import styles from "@/components/auth/AuthVisual.module.css";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const GoogleMark = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
    <path fill="#4285F4" d="M23.5 12.3c0-.8-.1-1.6-.2-2.3H12v4.5h6.5c-.3 1.5-1.1 2.8-2.4 3.7l3.7 2.9c2.2-2 3.7-5 3.7-8.8z" />
    <path fill="#34A853" d="M12 23c3.2 0 6-1.1 8-3l-3.7-2.9c-1.1.7-2.5 1.2-4.3 1.2-3 0-5.5-2-6.4-4.7L1.9 16C3.7 20.1 7.5 23 12 23z" />
    <path fill="#FBBC05" d="M5.6 14.7c-.3-.8-.4-1.7-.4-2.7s.1-1.9.4-2.7L1.9 6.4C.7 8.1 0 10 0 12s.7 3.9 1.9 5.6l3.7-2.9z" />
    <path fill="#EA4335" d="M12 5c1.6 0 3 .6 4.1 1.7l3.1-3.1C17.3 1.8 14.8 1 12 1 7.5 1 3.7 3.6 1.9 7.4l3.7 2.9C6.5 7.6 9 5 12 5z" />
  </svg>
);

export default function Masuk() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);
  const [mfa, setMfa] = useState(null);
  const [flash, setFlash] = useFlash();
  const emailRef = useRef(null);
  const pwId = useId();
  const pwMsgId = `${pwId}-msg`;

  useEffect(() => { if (!mfa) emailRef.current?.focus(); }, [mfa]);

  const validEmail = () => {
    if (EMAIL_RE.test(email.trim()) && email.length <= 120) return true;
    setErrors({ email: t("err.email") });
    emailRef.current?.focus();
    return false;
  };

  const done = (res) => navigate(!res.verified ? "/verifikasi-email" : getConsent() ? "/" : "/onboarding");

  const submit = async (e) => {
    e.preventDefault();
    if (busy || !validEmail()) return;
    setErrors({});
    setBusy(true);
    const res = await login({ email, password });
    setBusy(false);
    if (res.mfa) { setPassword(""); setMfa(res.mfa); }
    else if (res.error) setErrors({ form: t(res.error === "unavailable" ? "auth.login.unavailable" : "auth.login.failed") });
    else if (res.ok) done(res);
  };

  const google = async () => {
    if (busy) return;
    setBusy(true);
    setErrors({});
    const res = await googleSignIn();
    setBusy(false);
    if (res.mfa) setMfa(res.mfa);
    else if (res.error) setErrors({ form: t(res.error === "unavailable" ? "auth.login.unavailable" : "auth.google.failed") });
    else if (res.ok) done(res);
  };

  const verifyMfa = async (uid, code) => {
    const result = await completeMfaLogin(mfa, uid, code);
    if (result.ok) { setMfa(null); done(result); }
    return result;
  };

  const forgot = async () => {
    if (!validEmail()) return;
    setErrors({});
    await sendPasswordReset(email.trim());
    setFlash(t("auth.forgot.sent"));
  };

  return (
    <div className={styles.page} data-testid="login-canvas">
      <a href="#konten" className="skip-link" data-testid="skip-link">
        {t("skip")}
      </a>
      <header className={styles.desktopHeader} data-testid="login-desktop-header">
        <Link to="/" aria-label="AXORIS" className={styles.headerBrand}>
          <BrandLogo tone="dark" />
        </Link>
        <Link to="/" className={styles.backLink} data-testid="login-back-link">
          <ArrowLeft size={16} aria-hidden="true" />
          {t("auth.login.back")}
        </Link>
      </header>
      <main id="konten" data-testid="masuk-page" className={styles.main}>
        <div className={styles.frame} data-testid="login-frame">
          <div className={styles.brandRow} data-testid="login-brand-row">
            <Link to="/" aria-label="AXORIS" className={styles.brandLink}>
              <BrandLogo tone="dark" />
            </Link>
            <span className={styles.context}>{t("auth.login.access")}</span>
          </div>
          <div className={styles.heading}>
            <h1 className={styles.title} data-testid="auth-title">{t("auth.login.title")}</h1>
            <p className={styles.lede}>
              <span className={styles.mobileCopy}>{t("auth.login.stitchLede")}</span>
              <span className={styles.desktopCopy}>{t("auth.login.stitchLedeDesktop")}</span>
            </p>
          </div>
          <p role="status" data-testid="auth-flash" className={styles.flash}>
            {flash}
          </p>

          {mfa ? <MfaChallenge challenge={mfa} tone="dark" onVerify={verifyMfa} onCancel={() => { setMfa(null); setPassword(""); setErrors({}); emailRef.current?.focus(); }} /> : <>
          <form onSubmit={submit} noValidate className={styles.form}>
            <div className={styles.emailField}>
            <Field
              ref={emailRef}
              tone="dark"
              label={t("field.email")}
              type="email"
              name="email"
              autoComplete="email"
              placeholder={t("auth.login.emailPlaceholder")}
              maxLength={120}
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                setErrors({});
              }}
              error={errors.email}
              testId="login-email-input"
            />
              <Mail size={18} aria-hidden="true" className={styles.fieldIcon} />
            </div>
            <div className={styles.passwordField}>
              <div className={styles.labelRow}>
                <label htmlFor={pwId}>
                  {t("field.password")}
                </label>
                <button
                  type="button"
                  onClick={forgot}
                  data-testid="forgot-password-button"
                  className={styles.forgot}
                >
                  {t("auth.login.forgotLink")}
                </button>
              </div>
              <div className={styles.passwordControl}>
                <LockKeyhole size={18} aria-hidden="true" className={styles.passwordIcon} />
                <input
                  id={pwId}
                  data-testid="login-password-input"
                  type={showPw ? "text" : "password"}
                  name="password"
                  autoComplete="current-password"
                  maxLength={72}
                  value={password}
                  aria-describedby={errors.form ? pwMsgId : undefined}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    setErrors((x) => ({ ...x, form: undefined }));
                  }}
                  placeholder="••••••••"
                  className={styles.password}
                />
                <button
                  type="button"
                  onClick={() => setShowPw((v) => !v)}
                  aria-pressed={showPw}
                  aria-label={t(showPw ? "auth.hidePw" : "auth.showPw")}
                  data-testid="password-visibility-button"
                  className={styles.visibility}
                >
                  {showPw ? (
                    <EyeOff size={20} strokeWidth={1.5} aria-hidden="true" />
                  ) : (
                    <Eye size={20} strokeWidth={1.5} aria-hidden="true" />
                  )}
                </button>
              </div>
            </div>
            {errors.form && (
              <p
                id={pwMsgId}
                role="alert"
                data-testid="login-form-error"
                className={styles.error}
              >
                {errors.form}
              </p>
            )}
            <Button
              type="submit"
              variant="primary"
              busy={busy}
              disabled={busy}
              data-testid="login-submit-button"
              className={styles.submit}
            >
              {busy ? t("auth.login.busy") : t("auth.login.submit")}
              <ArrowRight size={18} aria-hidden="true" className={styles.submitArrow} />
            </Button>
          </form>

          <div className={styles.divider} aria-hidden="true" data-testid="login-divider">
            <span />
            <span>
              <span className={styles.mobileCopy}>{t("auth.login.googleDivider")}</span>
              <span className={styles.desktopCopy}>{t("auth.or")}</span>
            </span>
            <span />
          </div>
          <Button
            variant="secondary"
            onClick={google}
            disabled={busy}
            data-testid="google-signin-button"
            className={styles.google}
          >
            <GoogleMark />
            <span className={styles.mobileCopy}>{t("auth.google")}</span>
            <span className={styles.desktopCopy}>{t("auth.login.googleDesktop")}</span>
          </Button>

          <p className={styles.register}>
            <span>{t("auth.login.registerPrompt")}</span>{" "}
            <Link
              to="/daftar"
              data-testid="create-account-link"
              className={styles.registerLink}
            >
              <span className={styles.mobileCopy}>{t("auth.login.registerLink")}</span>
              <span className={styles.desktopCopy}>{t("auth.login.registerDesktop")}</span>
            </Link>
          </p>
          </>}
          <footer className={styles.footer} data-testid="login-footer">
            <Link to="/kebijakan" className={styles.policy} data-testid="login-policy-link">
              <FileText size={16} aria-hidden="true" />
              {t("auth.login.policy")}
            </Link>
            <p className={styles.help}>
              <span>{t("auth.login.help")}</span>{" "}
              <a href="mailto:halo@axoris.id" data-testid="support-email">halo@axoris.id</a>
            </p>
          </footer>
        </div>
      </main>
    </div>
  );
}
